import type { QueryKey } from '@tanstack/react-query';

import { useQuery } from '../../../ReactQuery';
import { get } from '../../../redux/api';
import { nuc } from '../../../utils';
import type { FullRevisionResponse } from '../../../../shared/types/routes/revisions';

/**
 * Fetches a full revision by locator.
 * This is also used by `useCurrentRevision` which composes its own `useQuery` call with polling and route awareness,
 * fetching the "current" revision based on the URL params.
 */
export function getRevision(revisionLocator: string) {
  return get<FullRevisionResponse>(`/api/revisions/${nuc(revisionLocator)}`, true);
}

export const getQueryKey = (revisionLocator: string): QueryKey => ['revisions', revisionLocator];

/**
 * Fetch a single revision by its locator.
 *
 * NOTE: if you are using this hook to fetch the current revision (ie: from a component in/under `/projects/:locator` in the app),
 * you should use `useCurrentRevision` instead. It automatically fetches the current revision based on the URL params,
 * and handles things like polling when the revision is in an unsteady state.
 *
 * @param {string} revisionLocator - The locator of the revision to fetch. No need to call nuc before passing it in here.
 * @param {boolean} shouldFetch - Allows you to delay the fetching of the revision. This can be useful if (for example) the full revision locator isn't available yet.
 */
export default function useRevision(
  revisionLocator: string,
  shouldFetch = true,
) {
  return useQuery({
    queryKey: getQueryKey(revisionLocator),
    queryFn: () => getRevision(revisionLocator),
    enabled: shouldFetch,
  });
}
